import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, Nav } from 'react-bootstrap';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import Flex from '@/components/common/Flex';
import IconItem from '@/components/common/icon/IconItem';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { useProject } from '../../context/ProjectContext';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import DataLoadError from '@/components/errors/DataLoadError';
import { PromptHistoryPanelSkeleton } from '../common/Skeleton';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../../utils/help/help-content';
import type { PromptExecutionHistoryResponse } from '../../types';
import { PromptHistoryRunsTable } from './PromptHistoryRunsTable';
import { PromptHistoryBrandsTable } from './PromptHistoryBrandsTable';
import {
  PromptPositionTrendChart,
  type PromptHistoryTrendMetric,
} from './PromptPositionTrendChart';

const TREND_TABS: {
  id: PromptHistoryTrendMetric;
  label: string;
  helpKey: string;
  subtitle: string;
}[] = [
  {
    id: 'position',
    label: 'ترند رتبه',
    helpKey: 'avg_position',
    subtitle: 'میانگین رتبه برند شما و رقبای ردیابی‌شده در هر روز (پایین‌تر بهتر است)',
  },
  {
    id: 'visibility',
    label: 'دیده‌شدن در AI',
    helpKey: 'ai_visibility',
    subtitle: 'درصد دیده‌شدن برند شما و رقبا در پاسخ‌های AI هر روز',
  },
  {
    id: 'shareOfVoice',
    label: 'سهم از دیده‌شدن',
    helpKey: 'share_of_voice',
    subtitle: 'سهم برند شما و رقبا از کل دیده‌شدن در هر روز',
  },
];

function SummaryCard({
  label,
  value,
  valueSuffix,
  hint,
  icon,
  color,
}: {
  label: string;
  value: string;
  valueSuffix?: ReactNode;
  hint?: string;
  icon: IconProp;
  color: string;
}) {
  return (
    <Card className="h-100">
      <Card.Body className="px-3 py-3">
        <Flex alignItems="center" justifyContent="between" className="gap-3">
          <div className="min-w-0">
            <p className="fs--2 fw-semibold text-uppercase text-500 mb-0">{label}</p>
            <p className="mt-1 mb-0 d-flex align-items-baseline flex-wrap gap-1">
              <span className="fs-1 fw-bold text-1000">{value}</span>
              {valueSuffix}
            </p>
            {hint ? <p className="mt-1 fs--1 text-500 mb-0">{hint}</p> : null}
          </div>
          <IconItem
            tag="div"
            icon={icon}
            bg={color}
            color="white"
            iconClass="fs-0"
            className="shadow-none flex-shrink-0"
          />
        </Flex>
      </Card.Body>
    </Card>
  );
}

export function PromptHistoryPanel({
  promptId,
  refreshKey = 0,
  onViewExecution,
}: {
  promptId: string;
  refreshKey?: number;
  onViewExecution?: (executionId: string) => void;
}) {
  const [history, setHistory] = useState<PromptExecutionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [trendMetric, setTrendMetric] = useState<PromptHistoryTrendMetric>('position');
  const { visibilityRange, customFrom, customTo, modelFilterId } = useProject();
  const activeTrendTab = TREND_TABS.find(tab => tab.id === trendMetric) ?? TREND_TABS[0];

  const rangeQueryOptions = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        providerIds: modelFilterId ? [modelFilterId] : undefined,
      }),
    [visibilityRange, customFrom, customTo, modelFilterId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void api
      .getPromptExecutionHistory(promptId, rangeQueryOptions)
      .then(data => {
        if (!cancelled) {
          setHistory(data);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promptId, refreshKey, rangeQueryOptions, retryNonce]);

  const deltaLabel = useMemo(() => {
    const delta = history?.summary.positionDelta;
    if (delta == null) return null;
    if (delta > 0) return `بهبود ${delta} رتبه نسبت به بازه قبل`;
    if (delta < 0) return `افت ${Math.abs(delta)} رتبه نسبت به بازه قبل`;
    return 'بدون تغییر نسبت به بازه قبل';
  }, [history?.summary.positionDelta]);

  if (error) {
    return (
      <DataLoadError
        compact
        onRetry={() => setRetryNonce(n => n + 1)}
        isRetrying={loading}
      />
    );
  }

  if (loading) {
    return <PromptHistoryPanelSkeleton />;
  }

  if (!history) return null;

  const { summary } = history;

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row row-cols-2 row-cols-sm-4 g-3">
        <div className="col">
          <SummaryCard
            label="اجراهای موفق"
            value={String(summary.completedCount)}
            valueSuffix={
              <span className="fs--2 fw-medium text-500">
                (از {summary.executionCount} اجرا)
              </span>
            }
            icon="check-circle"
            color="success"
          />
        </div>
        <div className="col">
          <SummaryCard
            label="میانگین رتبه شما"
            value={summary.ownBrandAvgPosition != null ? `#${summary.ownBrandAvgPosition}` : '—'}
            hint={deltaLabel ?? undefined}
            icon="sort-numeric-down"
            color="primary"
          />
        </div>
        <div className="col">
          <SummaryCard
            label="بهترین رتبه"
            value={summary.ownBrandBestPosition != null ? `#${summary.ownBrandBestPosition}` : '—'}
            icon="trophy"
            color="warning"
          />
        </div>
        <div className="col">
          <SummaryCard
            label="میانگین دیده‌شدن"
            value={summary.visibilityAvg != null ? `${summary.visibilityAvg}%` : '—'}
            icon="eye"
            color="info"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <Card.Header
          as={Flex}
          justifyContent="between"
          alignItems="center"
          className="ps-0 py-0 position-relative border-bottom"
        >
          <Nav as="ul" className="nav-tabs border-0 flex-wrap chart-tab tab-active-caret">
            {TREND_TABS.map(tab => (
              <Nav.Item key={tab.id} className="z-3" as="li">
                <Nav.Link
                  onClick={() => setTrendMetric(tab.id)}
                  className="mb-0 text-nowrap d-flex gap-1 align-items-center navLinkForceColor py-3 cursor-pointer"
                  active={trendMetric === tab.id}
                >
                  <span className={trendMetric === tab.id ? 'fw-bold' : 'fw-normal'}>{tab.label}</span>
                  <HelpTooltip html={getHelpHtml(tab.helpKey)} />
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Card.Header>
        <Card.Body>
          <p className="mb-3 fs--1 text-500">{activeTrendTab.subtitle}</p>
          <PromptPositionTrendChart history={history} metric={trendMetric} />
        </Card.Body>
      </Card>

      <Card className="overflow-hidden ag-grid-in-card">
        <Card.Header className="bg-body-tertiary border-bottom">
          <h3 className="fs-0 fw-semibold text-1000 mb-0">
            <span className="d-inline-flex align-items-center gap-1">
              جدول اجراها
              <HelpTooltip html={getHelpHtml('executed_at')} />
            </span>
          </h3>
          <p className="mt-1 fs--1 text-500 mb-0">
            جزئیات هر اجرا — برای مشاهده کامل روی ردیف کلیک کنید
          </p>
        </Card.Header>
        <Card.Body>
          <PromptHistoryRunsTable rows={history.executions} onViewExecution={onViewExecution} />
        </Card.Body>
      </Card>

      <Card className="overflow-hidden ag-grid-in-card">
        <Card.Header className="bg-body-tertiary border-bottom">
          <h3 className="fs-0 fw-semibold text-1000 mb-0">
            <span className="d-inline-flex align-items-center gap-1">
              دیده‌شدن برند
              <HelpTooltip html={getHelpHtml('brand_visibility')} />
            </span>
          </h3>
          <p className="mt-1 fs--1 text-500 mb-0">
            خلاصه عملکرد برندها در اجراهای این پرامپت طی بازه انتخاب‌شده
          </p>
        </Card.Header>
        <Card.Body>
          <PromptHistoryBrandsTable brands={history.brandSummary} totalExecutions={summary.completedCount} />
        </Card.Body>
      </Card>
    </div>
  );
}
